import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Briefcase, GraduationCap } from "@phosphor-icons/react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const techStack = [
  { name: "TypeScript", icon: "typescript/typescript-original.svg" },
  { name: "NestJS", icon: "nestjs/nestjs-original.svg" },
  { name: "Next.js", icon: "nextjs/nextjs-original.svg" },
  { name: "React", icon: "react/react-original.svg" },
  { name: "Node.js", icon: "nodejs/nodejs-original.svg" },
  { name: "Python", icon: "python/python-original.svg" },
  { name: "Elasticsearch", icon: "elasticsearch/elasticsearch-original.svg" },
  { name: "RabbitMQ", icon: "rabbitmq/rabbitmq-original.svg" },
  { name: "AWS", icon: "amazonwebservices/amazonwebservices-plain-wordmark.svg" },
  { name: "GCP", icon: "googlecloud/googlecloud-original.svg" },
  { name: "Docker", icon: "docker/docker-original.svg" },
  { name: "MongoDB", icon: "mongodb/mongodb-original.svg" },
  { name: "PostgreSQL", icon: "postgresql/postgresql-original.svg" },
  { name: "MySQL", icon: "mysql/mysql-original.svg" },
];

const DEVICON_BASE = "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/";

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="about" ref={ref} data-testid="about-section" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={{ staggerChildren: 0.1 }}
        >
          <motion.span
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-4"
          >
            About
          </motion.span>

          <motion.h2
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="about-heading"
            className="font-heading text-3xl sm:text-4xl font-bold text-[#EDEDED] tracking-tight mb-6"
          >
            Himanshu Goyal
          </motion.h2>

          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap gap-2 mb-8"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-[#A1A1AA] bg-[#121212] border border-white/[0.06] rounded-full px-3 py-1.5">
              <MapPin size={12} weight="regular" />
              India
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-[#A1A1AA] bg-[#121212] border border-white/[0.06] rounded-full px-3 py-1.5">
              <Briefcase size={12} weight="regular" />
              4+ years of experience
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-[#A1A1AA] bg-[#121212] border border-white/[0.06] rounded-full px-3 py-1.5">
              <GraduationCap size={12} weight="regular" />
              B.Sc. Mathematics
            </span>
          </motion.div>

          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10"
          >
            <p className="text-[15px] text-[#A1A1AA] leading-relaxed">
              AI-native Full Stack Engineer with a focus on building product systems that scale — from backend architecture and AI-powered workflows to frontend performance and enterprise infrastructure.
            </p>
            <p className="text-[15px] text-[#A1A1AA] leading-relaxed">
              I think in systems — how product, engineering, and operations connect. I care about architecture that evolves incrementally, AI that's embedded in real workflows, and teams that ship with clarity.
            </p>
          </motion.div>

          {/* Tech stack with mini logos */}
          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-xs font-mono text-[#A1A1AA]/60 uppercase tracking-wider mb-3 block">Core Stack</span>
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech) => (
                <span
                  key={tech.name}
                  className="inline-flex items-center gap-2 text-[12px] font-mono px-3 py-1.5 rounded-full border border-white/[0.08] text-[#A1A1AA] bg-white/[0.02] hover:border-white/[0.15] hover:text-[#EDEDED] transition-colors"
                >
                  <img
                    src={`${DEVICON_BASE}${tech.icon}`}
                    alt={tech.name}
                    className="w-3.5 h-3.5 object-contain"
                    style={{ filter: "brightness(0.85)" }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  {tech.name}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
